/**
 * Customer Settings V2 — "General" section: name, description,
 * website, logo, staff-only customer-type/billboard flags, and the
 * billing/shipping addresses subsection.
 *
 * Contains:
 * - GeneralSection (default export)
 * - AddressesSubsection — billing/shipping addresses, writes the
 *   projected fields straight into the `addresses` Formik section
 * - FormikCustomerTypeBridge — Formik adapter for CustomerTypeSelect
 */

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BillingShippingAddresses } from '@sophocles/components/BillingShippingAddresses';
import CustomerTypeSelect from '@sophocles/components/forms/CustomerTypeSelect';
import {
  CustomerLogoField,
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsSwitchField,
  SettingsTextField,
} from '@sophocles/views/settings/components';

// Billing / shipping addresses subsection.  Renders the existing
// BillingShippingAddresses component (shared with V1) and writes
// user-initiated edits straight into Formik's ``addresses`` section
// — the consolidated PATCH endpoint owns the upsert via
// ``upsert_primary_address``, so the V2 surface no longer has to
// fan out separate Address mutations on save.  The address fields
// the backend writes (``address_1`` / ``address_2`` / ``city`` /
// ``state`` / ``zipcode`` / ``country``) match the
// BillingShippingAddresses ``onChange`` payload 1:1 — derived
// metadata (``is_billing`` / ``customer`` / ``primary_address``) is
// resolved server-side and stripped here.
function AddressesSubsection({ customerId }) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const handleChange = React.useCallback(
    payload => {
      // BillingShippingAddresses fires the full address row (id,
      // customer, site_group, is_billing, primary_address, plus the
      // editable fields).  Project to the writable subset the
      // consolidated PATCH expects.
      const project = addr => {
        if (!addr) return undefined;
        return {
          address_1: addr.address_1 ?? '',
          address_2: addr.address_2 ?? '',
          city: addr.city ?? '',
          state: addr.state ?? '',
          zipcode: addr.zipcode ?? '',
          country: addr.country ?? '',
        };
      };
      setFieldValue('addresses', { billing: project(payload?.billing), shipping: project(payload?.shipping) }, false);
    },
    [setFieldValue],
  );
  return (
    <SettingsSubsection
      title={t('customerSettings.v2.addresses', 'Addresses')}
      description={t('customerSettings.v2.addressesDescription', 'Billing and shipping addresses for this customer.')}
      bordered
    >
      <Box sx={{ py: 1.5 }}>
        <BillingShippingAddresses customerId={customerId} onChange={handleChange} variant="filled" />
      </Box>
    </SettingsSubsection>
  );
}

// Formik-bound bridge for the existing CustomerTypeSelect.  Stores
// the selected type id; CustomerTypeSelect reads/writes via
// onChange + value props.  Pass `variant="filled"` + `hiddenLabel`
// so the field reads with the same chrome as the other Settings*
// fields in the General section.
function FormikCustomerTypeBridge({ name }) {
  const { values, setFieldValue } = useFormikContext();
  const value = name.split('.').reduce((acc, key) => acc?.[key], values);
  return (
    <CustomerTypeSelect
      name={name}
      value={value ?? ''}
      onChange={e => setFieldValue(name, e.target.value)}
      variant="filled"
      hiddenLabel
    />
  );
}

export default function GeneralSection({ customerId, settings, separateTenant, canEdit, onLogoChanged }) {
  const { t } = useTranslation();
  return (
    <SettingsSection
      id="settings-general"
      icon={<InfoOutlinedIcon />}
      title={t('customerSettings.v2.general', 'General')}
      description={t('customerSettings.v2.generalDescription', 'Customer name, description, website, and addresses.')}
    >
      <SettingsSubsection title={t('customerSettings.v2.identity', 'Identity')} bordered>
        <SettingsFieldRow
          label={t('general.name', 'Name')}
          description={t('customerSettings.v2.nameDescription', 'The customer name shown across the app.')}
          controlWidth={320}
          required
        >
          <SettingsTextField name="general.name" hiddenLabel fullWidth />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('general.description', 'Description')}
          description={t('customerSettings.v2.descriptionDescription', 'Short summary about this customer.')}
          controlWidth={400}
          required
        >
          <SettingsTextField name="general.description" hiddenLabel fullWidth multiline minRows={2} />
        </SettingsFieldRow>
        {!separateTenant && (
          <SettingsFieldRow
            label={t('general.website', 'Website')}
            description={t(
              'customerSettings.v2.websiteDescription',
              'External link for this customer (http:// or https:// added automatically on save).',
            )}
            controlWidth={400}
          >
            <SettingsTextField name="general.website" hiddenLabel fullWidth />
          </SettingsFieldRow>
        )}
        <SettingsFieldRow
          label={t('customerSettings.v2.logo', 'Logo')}
          description={t(
            'customerSettings.v2.logoDescription',
            'Shown next to this customer across the app. Changes apply immediately.',
          )}
          controlWidth={320}
        >
          <CustomerLogoField
            customerId={customerId}
            logoUrl={settings.general?.logo}
            disabled={!canEdit}
            onChanged={onLogoChanged}
          />
        </SettingsFieldRow>
        {/*
              ``customer_type`` and ``is_billboard`` are
              staff-only — server omits them from the response
              for non-staff users, so the rows render only when
              the keys are present on settings.general.
            */}
        {!separateTenant && settings.general?.customer_type !== undefined ? (
          <SettingsFieldRow
            label={t('customer.customerType', 'Customer Type')}
            description={t(
              'customerSettings.v2.customerTypeDescription',
              'Staff-only category for tenant-specific UX rules.',
            )}
            controlWidth={320}
          >
            <FormikCustomerTypeBridge name="general.customer_type" />
          </SettingsFieldRow>
        ) : null}
        {!separateTenant && settings.general?.is_billboard !== undefined ? (
          <SettingsFieldRow
            label={t('customer.isBillboard', 'Is Billboard')}
            description={t(
              'customerSettings.v2.isBillboardDescription',
              'Marks this customer as a billboard tenant. Affects terminology and feature gating.',
            )}
            controlWidth={80}
          >
            <SettingsSwitchField name="general.is_billboard" hideLabel />
          </SettingsFieldRow>
        ) : null}
      </SettingsSubsection>

      {!(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__) && <AddressesSubsection customerId={customerId} />}
    </SettingsSection>
  );
}
